import { useEffect, useState } from "react";
import { ShieldCheck, Copy, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/* MFA (2-step verification) enrollment card — TOTP authenticator apps.
   Flow: setup (QR + secret) -> enter code -> enabled + one-time recovery codes. */
const GC = { blue: "#4285F4", green: "#34A853", red: "#EA4335" };

export const MfaSettings = () => {
  const [status, setStatus] = useState(null); // {enabled}
  const [setup, setSetup] = useState(null); // {qr, secret}
  const [code, setCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/mfa/status").then((r) => setStatus(r.data)).catch(() => setStatus({ enabled: false }));
  }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mfa/setup");
      setSetup(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start setup"); }
    setBusy(false);
  };

  const enable = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/mfa/enable", { code: code.trim() });
      setRecovery(data.recovery_codes);
      setStatus({ enabled: true, verified: true });
      setSetup(null); setCode("");
      toast.success("2-step verification is ON");
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Invalid code"); }
    setBusy(false);
  };

  const disable = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.post("/mfa/disable", { code: disableCode.trim() });
      setStatus({ enabled: false }); setDisableCode(""); setRecovery(null);
      toast.success("2-step verification turned off");
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Invalid code"); }
    setBusy(false);
  };

  const copyRecovery = () => {
    navigator.clipboard?.writeText(recovery.join("\n"));
    toast.success("Recovery codes copied — store them somewhere safe");
  };

  if (!status) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div data-testid="mfa-settings">
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-[10px] font-800 uppercase px-1.5 py-0.5 rounded ${status.enabled ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-600"}`}
          data-testid="mfa-status-badge">
          {status.enabled ? "ON" : "OFF"}
        </span>
        <span className="text-sm text-zinc-700">
          {status.enabled
            ? "Signing in requires a code from your authenticator app once per session."
            : "Add a second sign-in step using a free authenticator app. On iPhone, the built-in Passwords app works and unlocks with Face ID."}
        </span>
      </div>

      {!status.enabled && !setup && (
        <button onClick={startSetup} disabled={busy} data-testid="mfa-setup-btn"
          className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-600 disabled:opacity-50"
          style={{ background: GC.blue }}>
          <Smartphone className="w-4 h-4" /> Turn on 2-step verification
        </button>
      )}

      {setup && (
        <div className="border border-blue-200 bg-blue-50/40 p-4 mt-2" data-testid="mfa-setup-panel">
          <h3 className="font-heading font-900 text-base mb-1" style={{ color: GC.blue }}>Set up 2-Step Verification</h3>
          <p className="text-xs text-zinc-600 mb-3">
            This works with a free <b>authenticator app</b> that shows a new 6-digit code every 30 seconds.
            <b> Face ID itself is not the code</b> — but on iPhone you can save the code in the built-in
            <b> Passwords app</b>, which unlocks with Face ID.
          </p>
          <ol className="text-sm text-zinc-800 space-y-2 mb-4">
            <li className="flex gap-2"><span className="shrink-0 w-5 h-5 rounded-full text-white text-xs font-800 flex items-center justify-center" style={{ background: GC.blue }}>1</span>
              <span><b>Get an authenticator</b> — iPhone: built-in <b>Passwords</b> app (uses Face ID) · Android/any: <b>Google Authenticator</b>, Authy or 1Password.</span></li>
            <li className="flex gap-2"><span className="shrink-0 w-5 h-5 rounded-full text-white text-xs font-800 flex items-center justify-center" style={{ background: GC.blue }}>2</span>
              <span><b>Add TravelerPulse</b> — scan the QR code below with that app (or tap the button if you're on your phone).</span></li>
            <li className="flex gap-2"><span className="shrink-0 w-5 h-5 rounded-full text-white text-xs font-800 flex items-center justify-center" style={{ background: GC.blue }}>3</span>
              <span><b>Confirm</b> — type the 6-digit code the app shows into the box below.</span></li>
          </ol>
          <div className="flex flex-wrap items-start gap-4 bg-white border border-zinc-200 p-3">
            <img src={setup.qr} alt="QR code — scan with your authenticator app" className="w-40 h-40 border border-zinc-200 bg-white" data-testid="mfa-qr" />
            <div className="text-xs text-zinc-600 space-y-2 min-w-[180px] flex-1">
              <a href={setup.otpauth_uri} data-testid="mfa-add-on-phone"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-white text-xs font-700 hover:opacity-90"
                style={{ background: GC.blue }}>
                <Smartphone className="w-3.5 h-3.5" /> On this phone? Tap to add automatically
              </a>
              <div>
                Can't scan or tap? In your authenticator choose "Enter key manually" and paste:
                <div className="font-mono text-sm text-zinc-900 mt-1 break-all select-all bg-zinc-100 border border-zinc-200 px-2 py-1.5" data-testid="mfa-secret">{setup.secret}</div>
              </div>
            </div>
          </div>
          <form onSubmit={enable} className="flex flex-wrap items-center gap-2 mt-4">
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code from the app"
              inputMode="numeric" autoComplete="one-time-code" data-testid="mfa-enable-code"
              className="border border-zinc-300 px-2.5 py-2 text-sm font-mono w-52 bg-white" />
            <button type="submit" disabled={busy || !code.trim()} data-testid="mfa-enable-btn"
              className="px-4 py-2 text-white text-sm font-600 disabled:opacity-50" style={{ background: GC.green }}>
              Confirm &amp; enable
            </button>
            <button type="button" onClick={() => { setSetup(null); setCode(""); }} className="text-xs text-muted-foreground hover:text-zinc-800">Cancel</button>
          </form>
        </div>
      )}

      {recovery && (
        <div className="border border-amber-300 bg-amber-50 p-4 mt-3" data-testid="mfa-recovery-codes">
          <div className="text-sm font-800 text-amber-800 mb-1">Recovery codes — shown only once</div>
          <p className="text-xs text-amber-800/80 mb-2">Each code works once if you lose access to your authenticator app. Save them now.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 font-mono text-sm">
            {recovery.map((c) => <span key={c} className="bg-white border border-amber-200 px-2 py-1 text-center">{c}</span>)}
          </div>
          <button onClick={copyRecovery} data-testid="mfa-copy-recovery"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-700 border border-amber-400 text-amber-800 px-2.5 py-1.5 hover:bg-amber-100">
            <Copy className="w-3.5 h-3.5" /> Copy all codes
          </button>
        </div>
      )}

      {status.enabled && !recovery && (
        <form onSubmit={disable} className="flex flex-wrap items-center gap-2 mt-2">
          <input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="Code to turn off"
            inputMode="numeric" data-testid="mfa-disable-code"
            className="border border-zinc-300 px-2.5 py-2 text-sm font-mono w-40" />
          <button type="submit" disabled={busy || !disableCode.trim()} data-testid="mfa-disable-btn"
            className="px-3.5 py-2 text-sm font-600 border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50">
            Turn off 2-step verification
          </button>
        </form>
      )}

      <p className="text-xs text-muted-foreground mt-3 flex items-start gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: GC.green }} />
        Works with any TOTP authenticator — iPhone Passwords app (Face ID), Google Authenticator, Authy,
        1Password. No SMS, no phone number, works offline and worldwide.
      </p>
    </div>
  );
};
